// Payment Service for East End Prep Sports
// Handles donations, fundraising, and merchandise payments

const PaymentService = {
  // Payment Types
  PaymentTypes: {
    DONATION: 'donation',
    FUNDRAISING: 'fundraising',
    MERCHANDISE: 'merchandise',
    REGISTRATION: 'registration'
  },

  // Donation Categories
  DonationCategories: {
    GENERAL: 'general',
    BOOSTER_CLUB: 'booster_club',
    EQUIPMENT: 'equipment',
    SCHOLARSHIPS: 'scholarships',
    FACILITIES: 'facilities'
  },

  // Process a payment (mock implementation)
  async processPayment(paymentData) {
    console.log('Processing payment:', paymentData);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock validation
    if (!paymentData.amount || paymentData.amount <= 0) {
      throw new Error('Invalid payment amount');
    }
    
    if (!paymentData.paymentMethod) {
      throw new Error('Payment method is required');
    }
    
    // Simulate payment processing
    const success = Math.random() > 0.1; // 90% success rate
    
    if (!success) {
      throw new Error('Payment processing failed. Please try again.');
    }
    
    // Return mock payment result
    return {
      id: `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      amount: paymentData.amount,
      currency: 'USD',
      status: 'succeeded',
      type: paymentData.type,
      category: paymentData.category,
      description: paymentData.description,
      donor: paymentData.donor,
      created_at: new Date().toISOString(),
      receipt_url: `https://receipts.eastendprep.edu/receipt_${Date.now()}`
    };
  },

  // Create donation payment
  async processDonation(donationData) {
    const paymentData = {
      ...donationData,
      type: this.PaymentTypes.DONATION,
      description: `Donation - ${donationData.category || 'General Fund'}`
    };
    
    return await this.processPayment(paymentData);
  },

  // Process fundraising event payment
  async processFundraisingPayment(fundraisingData) {
    const paymentData = {
      ...fundraisingData,
      type: this.PaymentTypes.FUNDRAISING,
      description: `Fundraising - ${fundraisingData.eventName}`
    };
    
    return await this.processPayment(paymentData);
  },

  // Process merchandise payment
  async processMerchandisePayment(merchandiseData) {
    const paymentData = {
      ...merchandiseData,
      type: this.PaymentTypes.MERCHANDISE,
      description: `Merchandise - ${merchandiseData.items.map(item => item.name).join(', ')}`
    };
    
    return await this.processPayment(paymentData);
  },

  // Get payment history
  async getPaymentHistory(filters = {}) {
    console.log('Fetching payment history:', filters);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock payment history data
    const mockPayments = [
      {
        id: 'pay_1234567890',
        amount: 100.00,
        currency: 'USD',
        status: 'succeeded',
        type: 'donation',
        category: 'booster_club',
        description: 'Donation - Booster Club',
        donor: {
          name: 'John Smith',
          email: 'john.smith@example.com'
        },
        created_at: '2024-03-15T10:30:00Z',
        receipt_url: 'https://receipts.eastendprep.edu/receipt_1234567890'
      },
      {
        id: 'pay_0987654321',
        amount: 250.00,
        currency: 'USD',
        status: 'succeeded',
        type: 'fundraising',
        category: 'event_tickets',
        description: 'Fundraising - Spring Gala Tickets',
        donor: {
          name: 'Sarah Johnson',
          email: 'sarah.johnson@example.com'
        },
        created_at: '2024-03-14T14:15:00Z',
        receipt_url: 'https://receipts.eastendprep.edu/receipt_0987654321'
      },
      {
        id: 'pay_1122334455',
        amount: 45.00,
        currency: 'USD',
        status: 'succeeded',
        type: 'merchandise',
        category: 'apparel',
        description: 'Merchandise - Team T-Shirt, Baseball Cap',
        donor: {
          name: 'Mike Davis',
          email: 'mike.davis@example.com'
        },
        created_at: '2024-03-13T09:45:00Z',
        receipt_url: 'https://receipts.eastendprep.edu/receipt_1122334455'
      }
    ];
    
    // Apply filters
    let filteredPayments = mockPayments;
    
    if (filters.type) {
      filteredPayments = filteredPayments.filter(payment => payment.type === filters.type);
    }
    
    if (filters.dateFrom) {
      filteredPayments = filteredPayments.filter(payment => 
        new Date(payment.created_at) >= new Date(filters.dateFrom)
      );
    }
    
    if (filters.dateTo) {
      filteredPayments = filteredPayments.filter(payment => 
        new Date(payment.created_at) <= new Date(filters.dateTo)
      );
    }
    
    return {
      payments: filteredPayments,
      total: filteredPayments.length,
      totalAmount: filteredPayments.reduce((sum, payment) => sum + payment.amount, 0)
    };
  },

  // Get donation statistics
  async getDonationStats() {
    console.log('Fetching donation statistics');
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 800));
    
    return {
      totalDonations: 15750.00,
      donationCount: 127,
      averageDonation: 124.02,
      monthlyGrowth: 12.5,
      topCategories: [
        { category: 'booster_club', amount: 6200.00, count: 45 },
        { category: 'equipment', amount: 4300.00, count: 32 },
        { category: 'scholarships', amount: 3100.00, count: 28 },
        { category: 'facilities', amount: 1650.00, count: 15 },
        { category: 'general', amount: 500.00, count: 7 }
      ],
      recentDonors: [
        { name: 'Anonymous', amount: 500.00, date: '2024-03-15' },
        { name: 'Smith Family', amount: 250.00, date: '2024-03-14' },
        { name: 'Johnson & Associates', amount: 1000.00, date: '2024-03-13' }
      ]
    };
  },

  // Get fundraising events
  async getFundraisingEvents() {
    console.log('Fetching fundraising events');
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 600));
    
    return [
      {
        id: 1,
        name: 'Spring Sports Gala',
        description: 'Annual fundraising dinner and auction',
        date: '2024-04-20T18:00:00Z',
        location: 'East End Prep Gymnasium',
        ticketPrice: 125.00,
        maxAttendees: 200,
        currentAttendees: 87,
        status: 'active',
        image: '/images/spring-gala.jpg',
        items: [
          { type: 'ticket', name: 'Individual Ticket', price: 125.00 },
          { type: 'ticket', name: 'Couple Ticket', price: 225.00 },
          { type: 'ticket', name: 'Table for 8', price: 900.00 }
        ]
      },
      {
        id: 2,
        name: 'Athletic Equipment Drive',
        description: 'Help us purchase new equipment for all sports teams',
        date: '2024-05-15T00:00:00Z',
        location: 'Online Campaign',
        targetAmount: 10000.00,
        currentAmount: 6750.00,
        status: 'active',
        image: '/images/equipment-drive.jpg',
        items: [
          { type: 'donation', name: 'Basketball Set', price: 500.00 },
          { type: 'donation', name: 'Soccer Goals', price: 750.00 },
          { type: 'donation', name: 'Track Equipment', price: 1000.00 }
        ]
      },
      {
        id: 3,
        name: 'Team Spirit Merchandise',
        description: 'Show your school pride with official merchandise',
        date: '2024-03-01T00:00:00Z',
        location: 'Online Store',
        status: 'ongoing',
        image: '/images/merchandise.jpg',
        items: [
          { type: 'merchandise', name: 'Team T-Shirt', price: 25.00 },
          { type: 'merchandise', name: 'Baseball Cap', price: 20.00 },
          { type: 'merchandise', name: 'Hoodie', price: 45.00 },
          { type: 'merchandise', name: 'Water Bottle', price: 15.00 }
        ]
      }
    ];
  },

  // Calculate processing fee
  calculateProcessingFee(amount) {
    // Typical credit card processing fee: 2.9% + $0.30
    return Math.round((amount * 0.029 + 0.30) * 100) / 100;
  },

  // Format currency
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  },

  // Validate payment data
  validatePaymentData(paymentData) {
    const errors = [];
    
    if (!paymentData.amount || paymentData.amount <= 0) {
      errors.push('Amount must be greater than 0');
    }
    
    if (paymentData.amount > 10000) {
      errors.push('Amount cannot exceed $10,000 per transaction');
    }
    
    if (!paymentData.paymentMethod) {
      errors.push('Payment method is required');
    }
    
    if (!paymentData.donor || !paymentData.donor.email) {
      errors.push('Donor email is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default PaymentService;